import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, Observable, Operation, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { Permissions } from 'shared/models/permission';

import { getLocalStorageKey } from '../common/local-storage';
import { logger } from '../common/logger';
import { handleSessionExpired } from '../common/session-expired';
import { config } from '../config';

type Role = keyof Permissions;

const webSocketUri =
    config.Env === 'production' ? `wss://${config.GraphAPIEndpoint}` : `ws://${config.GraphAPIEndpoint}`;
const httpUri =
    config.Env === 'production' ? `https://${config.GraphAPIEndpoint}` : `http://${config.GraphAPIEndpoint}`;

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const definition = getMainDefinition(operation.query);
    const unauthorized = 401;
    // Hasura can return authentication failures as GraphQL errors with HTTP 200.
    const authErrorCodes = ['access-denied', 'invalid-jwt', 'jwt-invalid-claims', 'jwt-missing-role-claims'];
    const unauthorizedError =
        graphQLErrors?.some(
            ({ extensions }) => typeof extensions?.code === 'string' && authErrorCodes.includes(extensions.code)
        ) ||
        (networkError && 'statusCode' in networkError && networkError.statusCode === unauthorized);
    if (definition.kind === 'OperationDefinition' && definition.operation !== 'subscription' && unauthorizedError) {
        handleSessionExpired();
    }

    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
            logger.warn('apollo graphql error', {
                requestData: {
                    locations,
                    message,
                    operation: operation?.operationName,
                    path,
                    variables: operation?.variables
                }
            })
        );
    }

    if (networkError) {
        logger.debug('apollo network error', {
            requestData: {
                operation: operation?.operationName,
                variables: operation?.variables
            }
        });
    }
});

const headers = (role?: Role) => {
    const { authToken } = getLocalStorageKey('session', { authToken: undefined });
    return {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(role ? { 'x-hasura-role': role } : {})
    };
};

const serverErrorStatus = 500;

const isMutation = (operation: Operation) => {
    const definition = getMainDefinition(operation.query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
};

// Holds a query until the browser reports a connection, so nothing is sent into a dead network.
// Mutations are not held: nothing in the UI shows a queued write, so they fail fast instead.
const offlineLink = new ApolloLink((operation, forward) => {
    if (navigator.onLine || isMutation(operation)) {
        return forward(operation);
    }
    return new Observable((observer) => {
        let subscription: { unsubscribe: () => void } | undefined;
        const handleOnline = () => {
            window.removeEventListener('online', handleOnline);
            subscription = forward(operation).subscribe(observer);
        };
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('online', handleOnline);
            subscription?.unsubscribe();
        };
    });
});

// Retries transient network failures with backoff. Mutations are never retried because a lost
// response may mean the server already applied them, and 4xx responses do not change on retry.
const retryLink = new RetryLink({
    attempts: {
        max: 5,
        retryIf: (error, operation) =>
            !!error && !isMutation(operation) && (!error.statusCode || error.statusCode >= serverErrorStatus)
    }
});

const httpLink = (role?: Role) => {
    const authLink = new ApolloLink((operation, forward) => {
        operation.setContext({ headers: { ...operation.getContext().headers, ...headers(role) } });
        return forward(operation);
    });
    return authLink.concat(new HttpLink({ fetch, uri: httpUri }));
};

const wsLink = (role?: Role) => {
    return new GraphQLWsLink(
        createClient({
            connectionParams: () => ({
                headers: headers(role)
            }),
            url: webSocketUri
        })
    );
};

const splitLink = (role?: Role) =>
    split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink(role),
        from([retryLink, offlineLink, httpLink(role)])
    );

export const client = (role?: Role) =>
    new ApolloClient({
        cache: new InMemoryCache(),
        connectToDevTools: true,
        link: from([errorLink, splitLink(role)])
    });
